import React from "react";
import { useTranslation } from "react-i18next";

export type CurrencyFormatterProps = {
	value: number | null | undefined;
	currency?: string;
	minimumFractionDigits?: number;
	maximumFractionDigits?: number;
};

/**
 * Formats a number as currency based on the current i18n locale.
 * Automatically handles decimal separators (. or ,) and thousand separators based on locale.
 *
 * @example
 * // In German locale (de): 1.234,56 €
 * // In English locale (en): €1,234.56
 * <CurrencyFormatter value={1234.56} />
 *
 * @example
 * // With custom currency
 * <CurrencyFormatter value={1234.56} currency="USD" />
 */
export function CurrencyFormatter({
	value,
	currency = "EUR",
	minimumFractionDigits = 2,
	maximumFractionDigits = 2,
}: CurrencyFormatterProps) {
	const { i18n } = useTranslation();

	if (value === null || value === undefined) {
		return null;
	}

	const formatted = formatCurrency(value, i18n.language, currency, {
		minimumFractionDigits,
		maximumFractionDigits,
	});

	return <span>{formatted}</span>;
}

/**
 * Utility function to format a number as currency for a given locale.
 * Can be used outside of React components.
 *
 * @example
 * formatCurrency(1234.56, "de", "EUR") // "1.234,56 €"
 * formatCurrency(1234.56, "en", "EUR") // "€1,234.56"
 */
export function formatCurrency(
	value: number,
	locale: string,
	currency: string = "EUR",
	options?: {
		minimumFractionDigits?: number;
		maximumFractionDigits?: number;
	},
): string {
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency,
		minimumFractionDigits: options?.minimumFractionDigits ?? 2,
		maximumFractionDigits: options?.maximumFractionDigits ?? 2,
	}).format(value);
}

/**
 * Hook to get a currency formatter function bound to the current locale.
 * Useful when you need to format multiple currency values.
 *
 * @example
 * const formatCurrency = useCurrencyFormatter();
 * const price1 = formatCurrency(100.5);
 * const price2 = formatCurrency(200.75);
 */
export function useCurrencyFormatter(currency: string = "EUR") {
	const { i18n } = useTranslation();

	return (
		value: number | null | undefined,
		options?: {
			minimumFractionDigits?: number;
			maximumFractionDigits?: number;
		},
	): string | null => {
		if (value == null) {
			return null;
		}

		return formatCurrency(value, i18n.language, currency, options);
	};
}
